// Every way of telling the farmer what to do, funnelled into one place: keyboard
// with edge detection, the mouse (position, clicks and the wheel), on-screen
// touch controls, and a gamepad. The game asks about *actions*, never key codes,
// which is what makes the remap panel in the settings screen a two-line change.
export type Action =
  | "up"
  | "down"
  | "left"
  | "right"
  | "use"
  | "interact"
  | "cycleSeed"
  | "inventory"
  | "help"
  | "build"
  | "settings"
  | "close"
  | "prevTool"
  | "nextTool"
  | "tool1"
  | "tool2"
  | "tool3"
  | "tool4"
  | "tool5"
  | "tool6"
  | "tool7";

export const DEFAULT_BINDINGS: Record<Action, string[]> = {
  up: ["KeyW", "ArrowUp"],
  down: ["KeyS", "ArrowDown"],
  left: ["KeyA", "ArrowLeft"],
  right: ["KeyD", "ArrowRight"],
  use: ["Space"],
  interact: ["KeyE"],
  cycleSeed: ["KeyQ"],
  inventory: ["KeyI"],
  help: ["KeyH"],
  build: ["KeyB"],
  settings: ["KeyO"],
  close: ["Escape"],
  prevTool: ["BracketLeft"],
  nextTool: ["BracketRight", "Tab"],
  tool1: ["Digit1"],
  tool2: ["Digit2"],
  tool3: ["Digit3"],
  tool4: ["Digit4"],
  tool5: ["Digit5"],
  tool6: ["Digit6"],
  tool7: ["Digit7"],
};

/** Actions the panels themselves listen for; they still fire while blocked. */
const UI_ACTIONS = new Set<Action>(["close", "help", "inventory", "settings", "build"]);

/** Gamepad button index → action. Standard mapping. */
const PAD_BUTTONS: Record<number, Action> = {
  0: "use",
  1: "interact",
  2: "cycleSeed",
  3: "inventory",
  4: "prevTool",
  5: "nextTool",
  9: "help",
  12: "up",
  13: "down",
  14: "left",
  15: "right",
};

const HANDLED = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Space",
  "Tab",
  "KeyE",
  "KeyI",
  "KeyH",
  "KeyB",
  "KeyO",
  "KeyQ",
]);

const BINDINGS_KEY = "farmstead.bindings.v1";

export interface PointerState {
  x: number;
  y: number;
  /** the pointer has moved since the last keyboard input */
  active: boolean;
}

export class Input {
  private readonly down = new Set<string>();
  private readonly pressed = new Set<string>();
  private readonly virtual = new Set<Action>();
  private readonly virtualPressed = new Set<Action>();
  private readonly padDown = new Set<Action>();
  private readonly padPressed = new Set<Action>();
  private bindings: Record<Action, string[]> = structuredClone(DEFAULT_BINDINGS);

  /** set while a modal panel owns the keyboard */
  blocked = false;
  readonly pointer: PointerState = { x: 0, y: 0, active: false };
  /** left and right clicks, cleared at the end of the frame */
  clicked = 0;
  rightClicked = 0;
  wheel = 0;
  /** listening for the next key press, for the remap UI */
  private capture: ((code: string) => void) | null = null;

  constructor(target: HTMLElement | Window = window, canvas?: HTMLElement) {
    this.loadBindings();

    target.addEventListener("keydown", (e) => {
      const ev = e as KeyboardEvent;
      if (ev.repeat) return;
      const code = ev.code;
      if (this.capture) {
        ev.preventDefault();
        const fn = this.capture;
        this.capture = null;
        fn(code);
        return;
      }
      if (HANDLED.has(code)) ev.preventDefault();
      this.down.add(code);
      this.pressed.add(code);
      // Only a key that actually does something hands targeting back to the
      // keyboard. Browsers emit plenty of keydowns nobody asked for — dead keys,
      // IME echoes, a headless driver's synthetic stream — and none of them mean
      // "stop aiming with the mouse".
      if (this.isBound(code)) this.pointer.active = false;
    });
    target.addEventListener("keyup", (e) => {
      this.down.delete((e as KeyboardEvent).code);
    });
    window.addEventListener("blur", () => {
      this.down.clear();
      this.virtual.clear();
    });

    const surface = canvas ?? (window as unknown as HTMLElement);
    surface.addEventListener?.("mousemove", (e) => {
      const ev = e as MouseEvent;
      this.pointer.x = ev.clientX;
      this.pointer.y = ev.clientY;
      this.pointer.active = true;
    });
    surface.addEventListener?.("mousedown", (e) => {
      const ev = e as MouseEvent;
      this.pointer.x = ev.clientX;
      this.pointer.y = ev.clientY;
      this.pointer.active = true;
      if (ev.button === 0) this.clicked++;
      if (ev.button === 2) this.rightClicked++;
    });
    surface.addEventListener?.("wheel", (e) => {
      this.wheel += Math.sign((e as WheelEvent).deltaY);
    }, { passive: true });
  }

  // ------------------------------------------------------------- bindings

  get keyBindings(): Record<Action, string[]> {
    return this.bindings;
  }

  /** Rebind an action to the next key the player presses. */
  captureNextKey(action: Action, done: (code: string) => void): void {
    this.capture = (code) => {
      if (code !== "Escape") {
        this.bindings[action] = [code];
        this.saveBindings();
      }
      done(code);
    };
  }

  resetBindings(): void {
    this.bindings = structuredClone(DEFAULT_BINDINGS);
    this.saveBindings();
  }

  private loadBindings(): void {
    try {
      const raw = localStorage.getItem(BINDINGS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<Record<Action, string[]>>;
      for (const [k, v] of Object.entries(parsed)) {
        if (Array.isArray(v) && v.length) this.bindings[k as Action] = v;
      }
    } catch {
      /* storage disabled — defaults are fine */
    }
  }

  private saveBindings(): void {
    try {
      localStorage.setItem(BINDINGS_KEY, JSON.stringify(this.bindings));
    } catch {
      /* storage disabled */
    }
  }

  // --------------------------------------------------------------- queries

  isDown(code: string): boolean {
    return !this.blocked && this.down.has(code);
  }

  /** True once, on the frame the key went down. Works while blocked. */
  wasPressed(code: string): boolean {
    return this.pressed.has(code);
  }

  /** True when some action is bound to this key code. */
  isBound(code: string): boolean {
    for (const codes of Object.values(this.bindings)) {
      if (codes.includes(code)) return true;
    }
    return false;
  }

  held(action: Action): boolean {
    if (this.blocked) return false;
    if (this.virtual.has(action) || this.padDown.has(action)) return true;
    return this.bindings[action].some((c) => this.down.has(c));
  }

  /** True once, on the frame the action fired. UI actions fire while blocked. */
  fired(action: Action): boolean {
    if (this.blocked && !UI_ACTIONS.has(action)) return false;
    return this.firedRaw(action);
  }

  /**
   * The same edge, ignoring `blocked`. For the few things that run *because*
   * the keyboard is blocked — paging through a conversation, mainly — and so
   * cannot go through `fired`.
   */
  firedRaw(action: Action): boolean {
    if (this.virtualPressed.has(action) || this.padPressed.has(action)) return true;
    return this.bindings[action].some((c) => this.pressed.has(c));
  }

  axis(): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (this.held("left")) x -= 1;
    if (this.held("right")) x += 1;
    if (this.held("up")) y -= 1;
    if (this.held("down")) y += 1;
    if (x === 0 && y === 0 && !this.blocked) {
      const stick = this.padAxis();
      x = stick.x;
      y = stick.y;
    }
    return { x, y };
  }

  // ----------------------------------------------------------------- touch

  /** Called by the on-screen controls the hud builds on touch devices. */
  setVirtual(action: Action, down: boolean): void {
    if (down) {
      if (!this.virtual.has(action)) this.virtualPressed.add(action);
      this.virtual.add(action);
    } else {
      this.virtual.delete(action);
    }
  }

  // --------------------------------------------------------------- gamepad

  private padAxisX = 0;
  private padAxisY = 0;

  private padAxis(): { x: number; y: number } {
    return { x: this.padAxisX, y: this.padAxisY };
  }

  /** Poll once per frame, before anything asks about actions. */
  pollGamepad(): void {
    this.padPressed.clear();
    const pads = navigator.getGamepads?.() ?? [];
    const pad = [...pads].find((p) => p && p.connected);
    if (!pad) {
      this.padDown.clear();
      this.padAxisX = 0;
      this.padAxisY = 0;
      return;
    }
    const dead = (v: number) => (Math.abs(v) < 0.25 ? 0 : v);
    this.padAxisX = dead(pad.axes[0] ?? 0);
    this.padAxisY = dead(pad.axes[1] ?? 0);
    const now = new Set<Action>();
    for (const [index, action] of Object.entries(PAD_BUTTONS)) {
      if (pad.buttons[Number(index)]?.pressed) now.add(action);
    }
    for (const a of now) if (!this.padDown.has(a)) this.padPressed.add(a);
    this.padDown.clear();
    for (const a of now) this.padDown.add(a);
  }

  endFrame(): void {
    this.pressed.clear();
    this.virtualPressed.clear();
    this.clicked = 0;
    this.rightClicked = 0;
    this.wheel = 0;
  }
}
